import * as React from 'react';
import { t } from '@citrite/translate';
import {
	AlertErrorIcon,
	AlertWarningIcon,
	Button,
	ButtonProps,
	FilterIcon,
	FlyoverBuilder,
	layout,
	palette,
	styleUtils,
	typography,
} from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { useServiceContinuityContext } from 'App/ServiceContinuity/Context';
import { ResourceIcon } from 'Components/ResourceIcon';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { FilterPopup } from '../FilterPopup';
import { FlexRow } from '../ui/Container';

interface LeaseResourcesListProps {
	resources: Resource[];
}

interface FilterOption {
	id: string;
	label: string;
	enabled: boolean;
	icon?: React.ReactNode;
}

// Function to get initial filter options with translations
const getInitialFilterOptions = (): FilterOption[] => [
	{
		id: 'expired',
		label: t('Workspace:service_continuity.expired_filter_label'),
		enabled: true,
		icon: <AlertErrorIcon size={16} />,
	},
	{
		id: 'expiring',
		label: t('Workspace:service_continuity.expiring_soon_filter_label'),
		enabled: true,
		icon: <AlertWarningIcon size={16} />,
	},
];

const Subtitle = styled.h3`
	font-weight: ${typography.weights.semiBold};
	font-size: ${typography.sizes.sml};
	color: ${palette.black};
	margin: 0;
`;

const ResourcesContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: ${layout.tinySpace};
	min-height: 0;
	max-height: 320px;
	width: 100%;
`;

const ResourcesHeader = styled(FlexRow)`
	justify-content: space-between;
`;

const ResourcesListContainer = styled.div`
	flex: 1;
	border-top: 1px solid ${palette.grey200};
	border-bottom: 1px solid ${palette.grey200};
	overflow-y: auto;
	min-height: 0;
	max-height: 284px;
	width: 100%;

	/* Webkit scrollbar styling */
	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: ${palette.grey100};
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb {
		background: ${palette.grey400};
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: ${palette.grey500};
	}
`;

const ResourceItem = styled.div`
	display: flex;
	align-items: center;
	gap: ${layout.smallSpace};
	padding: ${layout.smallSpace} ${layout.mediumSpace};
	height: 48px;
	background: ${palette.white};
	width: 100%;
	box-sizing: border-box;
	min-width: 0;
	justify-content: space-between;
`;

const ResourceName = styled.div`
	font-weight: ${typography.weights.regular};
	font-size: ${typography.sizes.sml};
	color: ${palette.grey900};
	overflow: hidden;
	text-overflow: ellipsis;

	/* Needed for -webkit-line-clamp */
	/* Use -webkit-box + -webkit-line-clamp to get true 2-line ellipsis. */
	/* There is no standard multi-line clamp; without these we lose proper 2-line truncation */
	/* and only single-line ellipsis / rough max-height clipping is possible. */
	display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
	word-break: break-word;
	min-width: 0;

	/* Fallback for browsers that don't support line-clamp */
	max-height: calc(${typography.sizes.sml} * 1.4 * 2); /* line-height * 2 lines */
	line-height: 1.4;

	/* Firefox fallback */
	@supports not (-webkit-line-clamp: 2) {
		display: block;
		position: relative;

		&::after {
			content: '...';
			position: absolute;
			right: 0;
			bottom: 0;
			background: ${palette.white};
			padding-left: 4px;
		}
	}
`;

const StyledDiv = styled.div`
	display: flex;
	align-items: center;
	gap: ${layout.smallSpace};
	min-width: 0;
`;

export const LeaseResourcesList: React.FC<LeaseResourcesListProps> = ({ resources }) => {
	const { shieldStatus } = useServiceContinuityContext();

	// State for filter options
	const [filters, setFilters] = React.useState<FilterOption[]>(getInitialFilterOptions);
	// Ref to store the button props for closing the flyover
	const flyoverButtonPropsRef = React.useRef<ButtonProps | null>(null);

	const isExpiredResource = React.useCallback(
		(resource: Resource): boolean => {
			return shieldStatus.expiredLeases.some(res => res === resource);
		},
		[shieldStatus?.expiredLeases]
	);

	const isExpiringResource = React.useCallback(
		(resource: Resource): boolean => {
			return shieldStatus.expiringLeases.some(res => res === resource);
		},
		[shieldStatus?.expiringLeases]
	);

	// Handle filter changes
	const handleFilterChange = React.useCallback((filterId: string, enabled: boolean) => {
		setFilters(prev =>
			prev.map(filter => (filter.id === filterId ? { ...filter, enabled } : filter))
		);
	}, []);

	// Handle dropdown close
	const handleCloseDropdown = React.useCallback(() => {
		// Close the flyover by triggering the button click
		if (flyoverButtonPropsRef.current?.onClick) {
			flyoverButtonPropsRef.current.onClick(
				{} as React.MouseEvent<HTMLButtonElement, MouseEvent>
			);
		}
	}, []);

	// Filter resources based on current filter settings
	const filteredResources = React.useMemo(() => {
		const enabledFilters = filters.filter(f => f.enabled).map(f => f.id);

		if (enabledFilters.length === filters.length) {
			// All filters enabled, show all resources
			return resources;
		}

		return resources.filter(resource => {
			const isExpired = isExpiredResource(resource);
			const isExpiring = isExpiringResource(resource);
			const isActive = !isExpired && !isExpiring;

			if (enabledFilters.includes('expired') && isExpired) {
				return true;
			}
			if (enabledFilters.includes('expiring') && isExpiring) {
				return true;
			}
			if (enabledFilters.includes('active') && isActive) {
				return true;
			}

			return false;
		});
	}, [resources, filters, isExpiredResource, isExpiringResource]);

	return (
		<ResourcesContainer>
			<ResourcesHeader>
				<Subtitle style={{ fontSize: '14px' }}>
					{t('Workspace:service_continuity.affected_resources', {
						count: filteredResources.length,
					})}
				</Subtitle>
				<FlyoverBuilder
					closeOnFlyoverClick={false}
					toggleOnClick
					style={{
						backgroundColor: palette.white,
						border: '0.5px solid',
						borderColor: palette.grey500,
						boxShadow: styleUtils.shadow2.boxShadow,
						borderRadius: layout.tinySpace,
					}}
					flyover={
						<FilterPopup
							filters={filters}
							onFilterChange={handleFilterChange}
							onClose={handleCloseDropdown}
						/>
					}
				>
					{(buttonProps: ButtonProps) => {
						// Store the button props so we can close the flyover programmatically
						flyoverButtonPropsRef.current = buttonProps;
						return (
							<Button.IconOnly
								{...buttonProps}
								icon={FilterIcon}
								title={t('Workspace:service_continuity.filters')}
								aria-label={t('Workspace:service_continuity.open_filters_menu')}
								size={16}
							/>
						);
					}}
				</FlyoverBuilder>
			</ResourcesHeader>
			<ResourcesListContainer>
				{filteredResources.map(resource => (
					<ResourceItem key={resource.id}>
						<StyledDiv>
							<ResourceIcon resource={resource} size={24} />
							<ResourceName>{resource.name}</ResourceName>
						</StyledDiv>
						{isExpiredResource(resource) && <AlertErrorIcon size={16} />}
						{isExpiringResource(resource) && <AlertWarningIcon size={16} />}
					</ResourceItem>
				))}
			</ResourcesListContainer>
		</ResourcesContainer>
	);
};
